import React, { useState, useEffect, useRef, useMemo, useCallback } from 'react';
import { useNavigate, createSearchParams } from 'react-router-dom';
import { ChevronLeft, ChevronRight, Plus } from 'lucide-react';
import { auth, db } from '../../firebase/firebaseConfig';
import { collection, getDocs, doc, getDoc } from 'firebase/firestore';
import {
  ensureFirestoreAuthReady,
  isStaleFirestorePermissionError,
} from '../../utils/firestoreAuthReady';
import DeckEditor from '../DeckManagement/DeckEditor';
import SpacedRepetition from '../SRS/SpacedRepetition';
import SRSDashboard from '../SRS/SRSDashboard';
import HomeProgressFooter from '../StudyMode/HomeProgressFooter';
import IconRenderer, { normalizeLanguageIconKey } from '../ui/IconRenderer';
import CookbookSnippetProgressStats from '../ui/CookbookSnippetProgressStats';
import { ReactComponent as HomeInProgressSnippetIcon } from '../../Icons/deck-icon.svg';
import { ReactComponent as HomePrimaryTabSnippetIcon } from '../../Icons/snippet-icon.svg';
import { ReactComponent as HomePrimaryTabRoadmapIcon } from '../../Icons/book.svg';
import { ReactComponent as RoadmapNodeIcon } from '../../Icons/roadmap-node-icon.svg';
import { ReactComponent as RefreshIcon } from '../../Icons/refresh-icon.svg';
import { ReactComponent as GuestDemoLearnIcon } from '../../Icons/roadmap-node-icon.svg';
import AchievementTrophyIcon from '../Achievements/AchievementTrophyIcon';
import * as roadmapService from '../../services/roadmapService';
import { formatLanguageDisplay, formatRoadmapTrackTitle, normalizeRoadmapLanguageKey } from '../../utils/roadmapDisplay';
import { formatSnippetMasteryCountAria } from '../../constants/snippetMasteryCopy';
import { computeRolePathOverview, rolePathResumeSubtitle } from '../../utils/rolePathResume';
import { useDirectPremiumCheck } from '../../hooks/useDirectPremiumCheck';
import { userCanAccessRoadmapTrack, getRoadmapTrackIdFromUserDeckData } from '../../utils/roadmapSubscription';
import { trackRolePathTrackStarted } from '../../utils/analytics';
import {
  fetchUserReviewSnapshot,
  fetchRoadmapTrackSnippetRows,
  buildRoadmapTrackSnippetRowsFromProgress,
  buildStudyableLibraryDueDecks,
  pruneOrphanCardProgress
} from '../../services/reviewQueueService';
import { useUserProfile, selectPrimaryRoleId } from '../../hooks/useUserProfile';
import { usePrimaryRoleId } from '../../hooks/usePrimaryRoleId';
import usePublishedRoles from '../../hooks/usePublishedRoles';
import {
  buildCardProgressMap,
  countCookbookProgressFromProgressMap,
  loadCookbookCardProgress,
  loadCookbookPracticeStartedCards
} from '../../utils/cookbookCardProgress';
import {
  ROADMAP_PATHS,
  ROADMAP_GRAPH_LANG_SEARCH_PARAM,
  ROADMAP_GRAPH_NAV_FROM_HOME_STATE,
  ROADMAP_GRAPH_OPEN_TRACK_PREVIEW_STATE_KEY,
  ROADMAP_GRAPH_RESUME_NODE_ID_STATE_KEY,
  ROADMAP_GRAPH_SELECTED_TRACK_ID_STATE_KEY,
  ROADMAP_ROLE_ID_SEARCH_PARAM,
  buildRoleRoadmapLearnPath
} from '../Roadmap/roadmapPaths';
import { findLearnRole } from '../Roadmap/roadmapLearnRoles';
import { writeRoadmapLearnHubMode, ROADMAP_LEARN_MODES } from '../Roadmap/roadmapLearnHub';
import { hasCompletedGuestStudy } from '../../utils/demoState';
import {
  cookbookSnippetSrsStatusPillVariant,
  formatCookbookIndexReviewLabel,
  isCookbookSnippetDueToday,
  isCookbookSnippetStarted
} from '../../utils/studySessionDotState';
import '../Achievements/UserAchievementStatsPanel.css';
import '../DeckManagement/DeckManager.css';
import './Home.css';

/** Matches ~1.3× home row scale (IconRenderer `large` is 32px). */
const HOME_LANG_ICON_ROW_STYLE = { width: '35px', height: '35px' };

/** Snippet cards shown in Home Snippets tab (by most recent SRS review). */
const HOME_RECENT_STUDIED_SNIPPETS_LIMIT = 3;
/** Load card subcollections for cookbooks shown in Home In Progress (matches display limit). */
const HOME_COOKBOOK_CARD_TOTAL_LOAD_LIMIT = 4;

/** Tracks shown per page on the Home role-path panel. */
const HOME_ROLE_PATH_TRACKS_PAGE_SIZE = 2;

const EMPTY_ROLE_PATH_TRACKS = [];

/** Title hints when `language` is missing on a deck / course (common for Learn imports). */
function inferLanguageHintFromHomeDeckTitle(title) {
  if (!title || typeof title !== 'string') return '';
  const t = title.toLowerCase();
  if (/\bjavascript\b/.test(t) || /\bnode\.?js\b/.test(t) || /\becmascript\b/.test(t)) return 'javascript';
  if (/\btypescript\b/.test(t)) return 'typescript';
  if (/\bpython\b/.test(t)) return 'python';
  if (/\brust\b/.test(t)) return 'rust';
  if (/\bgolang\b/.test(t)) return 'go';
  if (/\bc\+\+\b|\bcpp\b/.test(t)) return 'cpp';
  if (/\bc#\b/.test(t)) return 'csharp';
  if (/\bjava\b/.test(t)) return 'java';
  return '';
}

/** Deck says `javascript` but title is clearly Java — wrong metadata from some catalogs. */
function disambiguateJavaVsJsFromTitle(title) {
  if (!title || typeof title !== 'string') return false;
  const t = title.toLowerCase();
  const jsLike = /\bjavascript\b|\bjs\b|\bnode\.?js\b|\btypescript\b|\breact\b|\bvue\b|\bangular\b/.test(t);
  if (jsLike) return false;
  return /\bjava\b/.test(t);
}

function homeInProgressIconLanguage(item) {
  const raw = String(
    item?.language ?? item?.lang ?? item?.programmingLanguage ?? item?.programming_language ?? ''
  ).trim();
  const title = item?.name || item?.title || '';
  let key = normalizeLanguageIconKey(raw);
  if (key === 'javascript' && disambiguateJavaVsJsFromTitle(title)) key = 'java';
  if (!key) {
    key = normalizeLanguageIconKey(inferLanguageHintFromHomeDeckTitle(title));
  }
  if (!key) key = 'code';
  return key;
}

function homeLastSessionSnippetIconLanguage(row) {
  const raw = String(row?.language ?? '').trim();
  const title = `${row?.snippetTitle || ''} ${row?.deckPathLabel || ''}`;
  let key = normalizeLanguageIconKey(raw);
  if (key === 'javascript' && disambiguateJavaVsJsFromTitle(title)) key = 'java';
  if (!key) {
    key = normalizeLanguageIconKey(inferLanguageHintFromHomeDeckTitle(title));
  }
  if (!key) key = 'code';
  return key;
}

/**
 * Cookbook cards the user studied most recently (SRS `lastReviewed`), including roadmap cookbooks when allowed.
 */
async function buildRecentStudiedSnippetsForHome(userId, decksSnapshot, progressRows, isPremiumUser) {
  const deckById = new Map();
  for (const deckDoc of decksSnapshot.docs) {
    const data = deckDoc.data();
    const isCookbook = data.type === 'cookbook' || !data.type;
    if (!isCookbook) continue;
    deckById.set(deckDoc.id, { id: deckDoc.id, ...data });
  }

  const candidates = [];
  for (const p of progressRows) {
    if (!p.deckId || !p.cardId || !p.lastReviewed) continue;
    const t = new Date(p.lastReviewed).getTime();
    if (!Number.isFinite(t)) continue;
    candidates.push({ ...p, _sort: t });
  }
  candidates.sort((a, b) => b._sort - a._sort);

  const picked = [];
  const seenPair = new Set();
  for (const p of candidates) {
    const pairKey = `${p.deckId}_${p.cardId}`;
    if (seenPair.has(pairKey)) continue;
    const deck = deckById.get(p.deckId);
    if (!deck) continue;
    if (roadmapService.isRoadmapUserCookbook(deck)) {
      const tid = getRoadmapTrackIdFromUserDeckData(deck);
      if (!userCanAccessRoadmapTrack(isPremiumUser, tid)) continue;
    }
    seenPair.add(pairKey);
    picked.push(p);
    if (picked.length >= HOME_RECENT_STUDIED_SNIPPETS_LIMIT) break;
  }

  const rows = await Promise.all(
    picked.map(async (p) => {
      try {
        const deck = deckById.get(p.deckId);
        const cardRef = doc(db, `users/${userId}/decks/${p.deckId}/cards/${p.cardId}`);
        const cardSnap = await getDoc(cardRef);
        if (!cardSnap.exists()) return null;
        const cardData = cardSnap.data();
        if (!cardData.solution || !String(cardData.solution).trim()) return null;

        const pData = p && typeof p === 'object' ? p : null;
        const isStarted = isCookbookSnippetStarted(pData);
        const isDueToday = isCookbookSnippetDueToday(pData);
        const reviewLabel = formatCookbookIndexReviewLabel(pData);

        const deckDisplayName =
          typeof deck.name === 'string' && deck.name.trim() ? deck.name.trim() : 'Cookbook';
        const langKey =
          typeof deck.language === 'string' && deck.language.trim() ? deck.language.trim() : 'other';
        /** Same subtitle pattern as Profile → Skills (`UserAchievementStatsPanel`). */
        const deckPathLabel = `${deckDisplayName} · ${formatLanguageDisplay(langKey)}`;
        const roadmapTrackId = roadmapService.isRoadmapUserCookbook(deck)
          ? getRoadmapTrackIdFromUserDeckData(deck)
          : null;

        return {
          deckId: p.deckId,
          cardId: p.cardId,
          roadmapTrackId,
          snippetTitle:
            typeof cardData.title === 'string' && cardData.title.trim()
              ? cardData.title.trim()
              : 'Snippet',
          deckPathLabel,
          language: langKey,
          is_started: isStarted,
          is_due_today: isDueToday,
          reviewLabel
        };
      } catch (e) {
        console.error('buildRecentStudiedSnippetsForHome: card fetch failed', e);
        return null;
      }
    })
  );

  return rows.filter(Boolean);
}

/**
 * Group roadmap snippet progress rows into per-track summaries for the Home Tracks tab.
 *
 * @param {Array<{ roadmapTrackId?: string, deckName?: string, language?: string, mastered?: boolean, lastReviewed?: string|null }>} trackSnippetRows
 * @param {Array<{ trackId?: string, title?: string, language?: string|null }>} catalogTracks
 */
function buildHomeTracksFromSnippetRows(trackSnippetRows, catalogTracks) {
  const catalogById = new Map((catalogTracks || []).map((track) => [track.trackId, track]));
  const byTrackId = new Map();

  for (const row of trackSnippetRows) {
    const trackId = row.roadmapTrackId;
    if (!trackId) continue;

    if (!byTrackId.has(trackId)) {
      const catalogTrack = catalogById.get(trackId);
      byTrackId.set(trackId, {
        trackId,
        trackTitle: catalogTrack?.title || row.deckName || trackId,
        language: catalogTrack?.language || row.language || null,
        snippetTotal: 0,
        snippetStarted: 0,
        snippetMastered: 0,
        isComplete: false,
        lastActivityMs: 0
      });
    }

    const summary = byTrackId.get(trackId);
    summary.snippetStarted += 1;
    if (row.mastered) summary.snippetMastered += 1;
    const reviewedMs = row.lastReviewed ? new Date(row.lastReviewed).getTime() : 0;
    if (Number.isFinite(reviewedMs) && reviewedMs > summary.lastActivityMs) {
      summary.lastActivityMs = reviewedMs;
    }
  }

  return Array.from(byTrackId.values()).sort((a, b) => b.lastActivityMs - a.lastActivityMs);
}

/**
 * Attach card titles for SRS upcoming list (small N; one read per upcoming item).
 */
async function enrichUpcomingReviewsWithTitles(userId, items) {
  if (!items.length) return [];
  return Promise.all(
    items.map(async (progress) => {
      if (!progress.deckId || !progress.cardId) {
        return { ...progress, title: 'Card' };
      }
      try {
        const cardRef = doc(
          db,
          `users/${userId}/decks/${progress.deckId}/cards/${progress.cardId}`
        );
        const cardDoc = await getDoc(cardRef);
        if (cardDoc.exists()) {
          return {
            ...progress,
            title: cardDoc.data().title || `Card ${progress.cardId}`
          };
        }
      } catch (e) {
        console.error('enrichUpcomingReviewsWithTitles:', e);
      }
      return {
        ...progress,
        title: `Card ${progress.cardId}`
      };
    })
  );
}

const Home = ({
  userId,
  onStartStudying,
  setView,
  view,
  libraryNavNonce = 0,
  dueItemsCount = 0,
  onDueCountRefresh,
  isAnonymousGuest = false,
  guestShowBrowseTracksCta = false,
  onGuestBrowseTracks,
  onOpenCreation,
}) => {
  const navigate = useNavigate();
  const { isPremiumUser } = useDirectPremiumCheck(userId);
  const { data: profileData } = useUserProfile(isAnonymousGuest ? null : userId);
  const { data: fastPrimaryRoleId } = usePrimaryRoleId(isAnonymousGuest ? null : userId);
  const primaryRoleId =
    fastPrimaryRoleId ?? selectPrimaryRoleId(profileData?.userData) ?? null;
  const { roles: publishedRoles } = usePublishedRoles();
  const primaryRole = useMemo(() => {
    const role = findLearnRole(publishedRoles, primaryRoleId);
    if (!role || !primaryRoleId) return null;
    if (role.roleId) return role;
    return { ...role, roleId: primaryRoleId };
  }, [publishedRoles, primaryRoleId]);
  const [publishedTracks, setPublishedTracks] = useState([]);
  const [rolePathOverview, setRolePathOverview] = useState(null);
  const [rolePathOverviewLoading, setRolePathOverviewLoading] = useState(false);
  const [refreshKey, setRefreshKey] = useState(0);
  /** Tabs inside the bordered Home panel: role path vs cookbooks vs snippets. */
  const [homePrimaryTab, setHomePrimaryTab] = useState('roadmap');
  const [rolePathTrackPage, setRolePathTrackPage] = useState(1);
  const rolePathFetchGenerationRef = useRef('');
  const guestDefaultTracksTabAppliedRef = useRef(false);
  const [homeGuestTracks, setHomeGuestTracks] = useState([]);
  const [homeGuestTracksLoading, setHomeGuestTracksLoading] = useState(false);

  useEffect(() => {
    if (!primaryRole || isAnonymousGuest || homePrimaryTab !== 'role-path') {
      return;
    }

    let cancelled = false;
    roadmapService
      .loadPublishedTracks()
      .then((tracks) => {
        if (!cancelled) setPublishedTracks(tracks || []);
      })
      .catch(() => {
        if (!cancelled) setPublishedTracks([]);
      });
    return () => {
      cancelled = true;
    };
  }, [primaryRole, homePrimaryTab, isAnonymousGuest]);

  useEffect(() => {
    if (!primaryRole || !userId || isAnonymousGuest) {
      setRolePathOverview(null);
      setRolePathOverviewLoading(false);
      return;
    }

    if (homePrimaryTab !== 'role-path') {
      return;
    }

    let cancelled = false;
    const fetchGeneration = `${refreshKey}:${primaryRole.roleId}:${publishedTracks.length}`;
    const force = rolePathFetchGenerationRef.current !== fetchGeneration;
    rolePathFetchGenerationRef.current = fetchGeneration;

    setRolePathOverviewLoading(true);
    computeRolePathOverview(userId, primaryRole, publishedTracks, { force })
      .then((overview) => {
        if (!cancelled) setRolePathOverview(overview);
      })
      .catch(() => {
        if (!cancelled) setRolePathOverview(null);
      })
      .finally(() => {
        if (!cancelled) setRolePathOverviewLoading(false);
      });

    return () => {
      cancelled = true;
    };
  }, [primaryRole, userId, publishedTracks, refreshKey, homePrimaryTab, isAnonymousGuest]);

  const rolePathResumeView = useMemo(() => {
    if (!rolePathOverview) return null;
    return {
      trackId: rolePathOverview.resumeTrackId,
      trackTitle: rolePathOverview.resumeTrackTitle,
      language: rolePathOverview.resumeLanguage,
      nodeTitle: rolePathOverview.resumeNodeTitle,
      nodeId: rolePathOverview.resumeNodeId,
      tracksCompleted: rolePathOverview.tracksCompleted,
      tracksTotal: rolePathOverview.tracksTotal,
      allComplete: rolePathOverview.allComplete
    };
  }, [rolePathOverview]);

  const rolePathSubtitle = useMemo(() => {
    if (rolePathOverviewLoading && !rolePathOverview) return 'Open your role path in Learn';
    if (rolePathOverviewLoading) return 'Updating progress…';
    return rolePathResumeSubtitle(rolePathResumeView);
  }, [rolePathResumeView, rolePathOverviewLoading, rolePathOverview]);

  const rolePathActionLabel = useMemo(() => {
    if (rolePathOverviewLoading && !rolePathOverview) return 'Open';
    if (rolePathOverviewLoading) return '…';
    if (rolePathOverview?.allComplete) return 'View path';
    if (rolePathOverview?.resumeTrackId) return 'Continue';
    return 'View path';
  }, [rolePathOverview, rolePathOverviewLoading]);

  const rolePathTracks = rolePathOverview?.tracks ?? EMPTY_ROLE_PATH_TRACKS;
  const rolePathTrackTotalPages = Math.max(
    1,
    Math.ceil(rolePathTracks.length / HOME_ROLE_PATH_TRACKS_PAGE_SIZE)
  );

  useEffect(() => {
    setRolePathTrackPage(1);
  }, [primaryRoleId, rolePathTracks.length]);

  useEffect(() => {
    if (rolePathTrackPage > rolePathTrackTotalPages) {
      setRolePathTrackPage(rolePathTrackTotalPages);
    }
  }, [rolePathTrackPage, rolePathTrackTotalPages]);

  const rolePathTracksPageRows = useMemo(() => {
    const start = (rolePathTrackPage - 1) * HOME_ROLE_PATH_TRACKS_PAGE_SIZE;
    return rolePathTracks.slice(start, start + HOME_ROLE_PATH_TRACKS_PAGE_SIZE);
  }, [rolePathTracks, rolePathTrackPage]);

  const openPrimaryRolePath = useCallback(() => {
    if (!primaryRoleId) return;
    writeRoadmapLearnHubMode(ROADMAP_LEARN_MODES.role);

    const resume = rolePathResumeView;
    if (resume?.trackId) {
      const trackRef = {
        trackId: resume.trackId,
        language: resume.language,
        title: resume.trackTitle
      };
      // Free users may preview Pro tracks (cookbooks/snippets); study stays gated in Learn.
      const canStudy = userCanAccessRoadmapTrack(isPremiumUser, resume.trackId, trackRef);
      if (canStudy) {
        trackRolePathTrackStarted({
          role_id: primaryRoleId,
          track_id: resume.trackId,
          surface: 'home-role-continue'
        });
      }
      const langKey = normalizeRoadmapLanguageKey(resume.language || 'javascript');
      const params = createSearchParams({
        [ROADMAP_GRAPH_LANG_SEARCH_PARAM]: langKey,
        [ROADMAP_ROLE_ID_SEARCH_PARAM]: primaryRoleId
      });
      navigate(
        {
          pathname: ROADMAP_PATHS.graph,
          search: params.toString()
        },
        {
          state: {
            ...ROADMAP_GRAPH_NAV_FROM_HOME_STATE,
            [ROADMAP_GRAPH_SELECTED_TRACK_ID_STATE_KEY]: resume.trackId,
            [ROADMAP_GRAPH_OPEN_TRACK_PREVIEW_STATE_KEY]: true,
            ...(resume.nodeId
              ? { [ROADMAP_GRAPH_RESUME_NODE_ID_STATE_KEY]: resume.nodeId }
              : {})
          }
        }
      );
    } else {
      navigate(buildRoleRoadmapLearnPath(primaryRoleId));
    }
    setView?.('roadmap-menu');
  }, [primaryRoleId, navigate, setView, rolePathResumeView, isPremiumUser]);

  const openRoleTrackInLearn = useCallback(
    (trackRow) => {
      if (!primaryRoleId || !trackRow?.trackId) return;
      writeRoadmapLearnHubMode(ROADMAP_LEARN_MODES.role);

      const trackRef = {
        trackId: trackRow.trackId,
        language: trackRow.language,
        title: trackRow.trackTitle
      };
      const canStudy = userCanAccessRoadmapTrack(isPremiumUser, trackRow.trackId, trackRef);
      if (canStudy) {
        trackRolePathTrackStarted({
          role_id: primaryRoleId,
          track_id: trackRow.trackId,
          surface: 'home-role-path'
        });
      }
      const langKey = normalizeRoadmapLanguageKey(trackRow.language || 'javascript');
      const params = createSearchParams({
        [ROADMAP_GRAPH_LANG_SEARCH_PARAM]: langKey,
        [ROADMAP_ROLE_ID_SEARCH_PARAM]: primaryRoleId
      });
      navigate(
        {
          pathname: ROADMAP_PATHS.graph,
          search: params.toString()
        },
        {
          state: {
            ...ROADMAP_GRAPH_NAV_FROM_HOME_STATE,
            [ROADMAP_GRAPH_SELECTED_TRACK_ID_STATE_KEY]: trackRow.trackId,
            [ROADMAP_GRAPH_OPEN_TRACK_PREVIEW_STATE_KEY]: true
          }
        }
      );
      setView?.('roadmap-menu');
    },
    [primaryRoleId, navigate, setView, isPremiumUser]
  );

  const openGuestTrackInLearn = useCallback(
    (trackRow) => {
      if (!trackRow?.trackId) return;
      writeRoadmapLearnHubMode(ROADMAP_LEARN_MODES.language);

      const langKey = normalizeRoadmapLanguageKey(trackRow.language || 'javascript');
      const params = createSearchParams({
        [ROADMAP_GRAPH_LANG_SEARCH_PARAM]: langKey
      });
      navigate(
        {
          pathname: ROADMAP_PATHS.graph,
          search: params.toString()
        },
        {
          state: {
            ...ROADMAP_GRAPH_NAV_FROM_HOME_STATE,
            [ROADMAP_GRAPH_SELECTED_TRACK_ID_STATE_KEY]: trackRow.trackId,
            [ROADMAP_GRAPH_OPEN_TRACK_PREVIEW_STATE_KEY]: true
          }
        }
      );
      setView?.('roadmap-menu');
    },
    [navigate, setView]
  );

  const guestExpectsTrackTab =
    isAnonymousGuest && (hasCompletedGuestStudy() || dueItemsCount > 0);
  const showHomeTracksTab =
    (isAnonymousGuest || !primaryRole) &&
    (homeGuestTracks.length > 0 || (guestExpectsTrackTab && homeGuestTracksLoading));

  const [recentItems, setRecentItems] = useState([]);
  const [recentStudiedSnippets, setRecentStudiedSnippets] = useState([]);
  const [selectedDeck, setSelectedDeck] = useState(null);
  const [studySnippet, setStudySnippet] = useState(null);
  const srs = useMemo(() => new SpacedRepetition(userId), [userId]);
  const [selectedDeckCards, setSelectedDeckCards] = useState(null);
  const [deckPreviewMode, setDeckPreviewMode] = useState(false);
  const [viewMode, setViewMode] = useState('recents'); // 'recents' or 'srs'
  const [isLoading, setIsLoading] = useState(() => !isAnonymousGuest);
  const [error, setError] = useState(null);
  const [retryCount, setRetryCount] = useState(0);
  const MAX_RETRIES = 3;
  const RETRY_DELAY = 2000; // 2 seconds
  const IN_PROGRESS_LIMIT = 4; // max items to show in In Progress
  /** Cap cookbook pool for sorting — limits expensive per-deck card reads on Home */
  const IN_PROGRESS_POOL_SIZE = 16;
  /** Cookbook row always reserves this many slots (filled + dashed placeholders). */
  const HOME_COOKBOOK_SLOT_COUNT = IN_PROGRESS_LIMIT;

  const srsRoadmapDeckTrackIdsRef = useRef(new Map());
  const homeCookbookProgressMapRef = useRef(new Map());
  const [practiceLoadingDeckId, setPracticeLoadingDeckId] = useState(null);
  const [dueDecks, setDueDecks] = useState([]);
  const [upcomingReviews, setUpcomingReviews] = useState([]);
  const [trackSnippetRows, setTrackSnippetRows] = useState([]);
  const [trackUpcomingReviews, setTrackUpcomingReviews] = useState([]);
  const [loadingSrs, setLoadingSrs] = useState(false);
  /** SRS-only load failure; must not use global `error` (that hides the whole Home). */
  const [srsLoadError, setSrsLoadError] = useState(null);

  // Cleanup function to reset state
  const cleanup = () => {
    setRecentItems([]);
    setRecentStudiedSnippets([]);
    setSelectedDeck(null);
    setStudySnippet(null);
    setSelectedDeckCards(null);
    setDeckPreviewMode(false);
    setViewMode('recents');
    setHomePrimaryTab('roadmap');
    setRefreshKey(0);
    setIsLoading(true);
    setError(null);
    setRetryCount(0);
    setSrsLoadError(null);
    setHomeGuestTracks([]);
    setHomeGuestTracksLoading(false);
    guestDefaultTracksTabAppliedRef.current = false;
    srsRoadmapDeckTrackIdsRef.current = new Map();
  };

  // Leaving deck editor when the app shell bumps Library nav (sync with DeckManager)
  useEffect(() => {
    if (libraryNavNonce > 0) {
      setSelectedDeck(null);
      setDeckPreviewMode(false);
      setSelectedDeckCards(null);
    }
  }, [libraryNavNonce]);

  // Effect to handle userId changes (logout/login)
  useEffect(() => {
    cleanup();

    return () => {
      cleanup();
    };
  }, [userId]);

  // Pick up cross-tab / external "home needs refresh" (interval only checks the flag)
  useEffect(() => {
    if (!userId) return;

    const checkRefreshFlag = () => {
      const needsRefresh = localStorage.getItem(`${userId}_needsRefresh`) === 'true';
      if (needsRefresh) {
        localStorage.removeItem(`${userId}_needsRefresh`);
        setRefreshKey((k) => k + 1);
      }
    };

    checkRefreshFlag();
    const interval = setInterval(checkRefreshFlag, 5000);

    return () => clearInterval(interval);
  }, [userId]);

  // Update viewMode based on the App's view state
  useEffect(() => {
    if (setView) {
      // If the App's view is 'home', ensure we're showing 'recents'
      if (view === 'home') {
        setViewMode('recents');
      }
      // If the App's view is 'home-srs', ensure we're showing 'srs'
      else if (view === 'home-srs') {
        setViewMode('srs');
      }
    }
  }, [view, setView]);

  const getMasteryColor = (mastery) => {
    if (mastery <= 33) return 'low-mastery';
    if (mastery <= 66) return 'medium-mastery';
    return 'high-mastery';
  };

  // Function to handle retry logic
  const handleRetry = () => {
    if (retryCount < MAX_RETRIES) {
      setRetryCount(prev => prev + 1);
      setError(null);
      if (isAnonymousGuest) {
        loadGuestHomeActivity();
      } else {
        loadRecentItems();
      }
    } else {
      setError('Failed to load data after multiple attempts. Please try refreshing the page.');
    }
  };

  const loadGuestHomeActivity = async () => {
    if (!userId) return;

    const authReady = await ensureFirestoreAuthReady(userId);
    if (!authReady) return;

    setIsLoading(true);
    setHomeGuestTracksLoading(true);
    try {
      const [[decksSnapshot, progressRows], catalogTracks] = await Promise.all([
        fetchUserReviewSnapshot(userId, srs),
        roadmapService.loadPublishedTracks().catch(() => [])
      ]);

      if (auth.currentUser?.uid !== userId) return;

      const [studiedSnippets, trackRows] = await Promise.all([
        buildRecentStudiedSnippetsForHome(userId, decksSnapshot, progressRows, isPremiumUser),
        Promise.resolve(
          buildRoadmapTrackSnippetRowsFromProgress(decksSnapshot, progressRows, srs)
        )
      ]);

      if (auth.currentUser?.uid !== userId) return;

      const accessibleTrackRows = trackRows.filter((row) =>
        userCanAccessRoadmapTrack(isPremiumUser, row.roadmapTrackId)
      );

      setRecentItems([]);
      setRecentCourses([]);
      setRecentStudiedSnippets(studiedSnippets);
      setHomeGuestTracks(buildHomeTracksFromSnippetRows(accessibleTrackRows, catalogTracks));
      setError(null);
    } catch (error) {
      if (isStaleFirestorePermissionError(userId, error)) return;
      console.error('Error loading guest home activity:', error);
      setError('Failed to load recent items. Retrying...');
      setRecentStudiedSnippets([]);
      setHomeGuestTracks([]);
    } finally {
      if (auth.currentUser?.uid === userId) {
        setIsLoading(false);
        setHomeGuestTracksLoading(false);
      }
    }
  };

  useEffect(() => {
    if (!userId) return;
    if (isAnonymousGuest) {
      if (!hasCompletedGuestStudy() && dueItemsCount <= 0) {
        setRecentItems([]);
        setRecentStudiedSnippets([]);
        setHomeGuestTracks([]);
        setHomeGuestTracksLoading(false);
        setIsLoading(false);
        setError(null);
        return;
      }
      loadGuestHomeActivity();
      return;
    }
    loadRecentItems();
  }, [userId, refreshKey, isAnonymousGuest]);

  /** Guest home: reload when due count first becomes positive (avoids re-fetch on every badge tick). */
  const prevGuestDueCountRef = useRef(null);
  useEffect(() => {
    if (!userId || !isAnonymousGuest) {
      prevGuestDueCountRef.current = null;
      return;
    }
    const prev = prevGuestDueCountRef.current;
    prevGuestDueCountRef.current = dueItemsCount;
    if (prev === null) return;
    if (prev <= 0 && dueItemsCount > 0) {
      loadGuestHomeActivity();
    }
  }, [dueItemsCount, userId, isAnonymousGuest]);

  useEffect(() => {
    if (!isAnonymousGuest || guestDefaultTracksTabAppliedRef.current) return;
    if (homeGuestTracks.length === 0) return;
    guestDefaultTracksTabAppliedRef.current = true;
    setHomePrimaryTab('tracks');
  }, [isAnonymousGuest, homeGuestTracks.length]);

  // Effect to handle retry logic
  useEffect(() => {
    if (error && retryCount < MAX_RETRIES) {
      const timer = setTimeout(() => {
        handleRetry();
      }, RETRY_DELAY);
      return () => clearTimeout(timer);
    }
  }, [error, retryCount]);

  // Load SRS when Review tab is active; cancel in-flight work if user navigates away (avoids stray errors).
  useEffect(() => {
    if (viewMode !== 'srs' || !userId) {
      return;
    }

    let cancelled = false;

    (async () => {
      setLoadingSrs(true);
      setSrsLoadError(null);
      try {
        const [decksSnap, progressRows] = await fetchUserReviewSnapshot(userId, srs);
        if (cancelled) return;

        const deckIsRoadmap = new Map();
        const deckRoadmapTrackById = new Map();
        const roadmapDeckTrackIdsForStudy = new Map();
        const decksList = [];
        decksSnap.forEach((d) => {
          const data = d.data();
          decksList.push({ id: d.id, ...data });
          const isRm = roadmapService.isRoadmapUserCookbook({ id: d.id, ...data });
          deckIsRoadmap.set(d.id, isRm);
          const tid = getRoadmapTrackIdFromUserDeckData(data);
          deckRoadmapTrackById.set(d.id, tid);
          if (isRm) {
            roadmapDeckTrackIdsForStudy.set(d.id, tid);
          }
        });
        srsRoadmapDeckTrackIdsRef.current = roadmapDeckTrackIdsForStudy;

        const prunedOrphans = await pruneOrphanCardProgress(userId, progressRows, srs);
        if (cancelled) return;

        let activeProgressRows = progressRows;
        if (prunedOrphans > 0) {
          const refreshed = await fetchUserReviewSnapshot(userId, srs);
          if (cancelled) return;
          activeProgressRows = refreshed[1];
          onDueCountRefresh?.();
        }

        const studyableDueDecks = await buildStudyableLibraryDueDecks(
          userId,
          decksList,
          activeProgressRows,
          srs,
          deckIsRoadmap
        );
        if (cancelled) return;
        setDueDecks(studyableDueDecks);

        const upcomingBase = srs.buildUpcomingReviewsFromProgress(activeProgressRows, 7);
        const upcoming = await enrichUpcomingReviewsWithTitles(userId, upcomingBase);
        if (cancelled) return;

        setUpcomingReviews(upcoming.filter((u) => !deckIsRoadmap.get(u.deckId)));
        setTrackUpcomingReviews(
          upcoming.filter((u) => {
            if (!deckIsRoadmap.get(u.deckId)) return false;
            return userCanAccessRoadmapTrack(
              isPremiumUser,
              deckRoadmapTrackById.get(u.deckId)
            );
          })
        );

        // Studyable track rows only — must match Study Mode dueOnly card resolution.
        const trackRows = await fetchRoadmapTrackSnippetRows(
          userId,
          decksSnap,
          activeProgressRows,
          srs
        );
        if (cancelled) return;

        setTrackSnippetRows(
          trackRows.filter((row) => userCanAccessRoadmapTrack(isPremiumUser, row.roadmapTrackId))
        );
      } catch (err) {
        console.error('Error loading SRS dashboard data:', err);
        if (!cancelled) {
          setSrsLoadError('Could not load review data. Check your connection and try again.');
          setDueDecks([]);
          setUpcomingReviews([]);
          setTrackUpcomingReviews([]);
          setTrackSnippetRows([]);
        }
      } finally {
        if (!cancelled) {
          setLoadingSrs(false);
        }
      }
    })();

    return () => {
      cancelled = true;
    };
  }, [viewMode, refreshKey, userId, srs, isPremiumUser, onDueCountRefresh]);

  const handleStudyDeck = useCallback(
    (deckId, { startCardId = null, dueOnly = true } = {}) => {
      if (!deckId) return;
      const roadmapTrackMap = srsRoadmapDeckTrackIdsRef.current;
      if (roadmapTrackMap.has(deckId)) {
        const trackId = roadmapTrackMap.get(deckId);
        if (!userCanAccessRoadmapTrack(isPremiumUser, trackId)) {
          window.dispatchEvent(new CustomEvent('navigateToSubscription'));
          return;
        }
      }
      onStartStudying({
        deckId,
        dueOnly,
        ...(startCardId ? { startCardId } : {})
      });
    },
    [onStartStudying, isPremiumUser]
  );

  const handleOpenRecentSnippet = useCallback(
    (row) => {
      if (!row?.deckId || !row?.cardId) return;
      if (row.roadmapTrackId && !userCanAccessRoadmapTrack(isPremiumUser, row.roadmapTrackId)) {
        window.dispatchEvent(new CustomEvent('navigateToSubscription'));
        return;
      }
      onStartStudying({
        deckId: row.deckId,
        startCardId: row.cardId,
        studyReturnContext: {
          achievementsPanelView: 'skills',
          achievementsLanguageFilter: ''
        }
      });
    },
    [onStartStudying, isPremiumUser]
  );

  const handlePracticeStartedCookbook = useCallback(
    async (e, item) => {
      e.stopPropagation();
      const deckId = item?.id;
      if (!deckId || !userId || !onStartStudying || practiceLoadingDeckId) return;
      if (!item.snippetsStarted) return;

      setPracticeLoadingDeckId(deckId);
      try {
        let progressMap = homeCookbookProgressMapRef.current;
        if (!(progressMap instanceof Map) || progressMap.size === 0) {
          const progressRows = await srs.fetchAllCardProgressRows();
          progressMap = buildCardProgressMap(progressRows);
          homeCookbookProgressMapRef.current = progressMap;
        }
        const cards = await loadCookbookPracticeStartedCards(userId, deckId, progressMap);
        if (cards.length === 0) return;

        onStartStudying({
          practiceStartedSession: true,
          deckId,
          cards,
          startCardId: cards[0].id
        });
      } catch (err) {
        console.error('Home: failed to start cookbook practice session', err);
      } finally {
        setPracticeLoadingDeckId(null);
      }
    },
    [userId, onStartStudying, practiceLoadingDeckId, srs]
  );

  const loadRecentItems = async () => {
    if (!userId) return;

    const authReady = await ensureFirestoreAuthReady(userId);
    if (!authReady) return;

    setIsLoading(true);
    setRecentStudiedSnippets([]);
    try {
      const [decksSnapshot, progressRows] = await fetchUserReviewSnapshot(userId, srs);

      if (auth.currentUser?.uid !== userId) return;

      const loadedCookbooks = decksSnapshot.docs
        .filter((d) => d.data().type === 'cookbook')
        .map((d) => ({
          id: d.id,
          type: 'cookbook',
          ...d.data()
        }))
        .filter((item) => !roadmapService.isRoadmapUserCookbook(item));

      loadedCookbooks.sort((a, b) => getSortTime(b) - getSortTime(a));
      const pool = loadedCookbooks.slice(0, IN_PROGRESS_POOL_SIZE);
      setRecentItems(pool);
      setError(null);
      setIsLoading(false);

      const progressMap = buildCardProgressMap(progressRows);
      homeCookbookProgressMapRef.current = progressMap;

      const itemsFromProgress = pool.map((item) => {
        const partial = countCookbookProgressFromProgressMap(item.id, progressMap);
        return {
          ...item,
          snippetsStarted: partial.started,
          snippetsStartedNotDue: partial.startedNotDue,
          snippetsMastered: partial.mastered,
          cardCount: undefined
        };
      });

      const [studiedSnippets] = await Promise.all([
        buildRecentStudiedSnippetsForHome(userId, decksSnapshot, progressRows, isPremiumUser)
      ]);
      setRecentItems(itemsFromProgress);
      setRecentStudiedSnippets(studiedSnippets);

      const cardTotalCandidates = [...itemsFromProgress]
        .sort((a, b) => getSortTime(b) - getSortTime(a))
        .slice(0, HOME_COOKBOOK_CARD_TOTAL_LOAD_LIMIT);

      if (cardTotalCandidates.length > 0) {
        const loadedTotals = await Promise.all(
          cardTotalCandidates.map(async (item) => {
            try {
              const progress = await loadCookbookCardProgress(userId, item.id, progressMap);
              return { id: item.id, ...progress };
            } catch (err) {
              console.error('Error loading progress for item:', err);
              return null;
            }
          })
        );
        const byId = new Map(loadedTotals.filter(Boolean).map((row) => [row.id, row]));
        setRecentItems((prev) =>
          prev.map((item) => {
            const loaded = byId.get(item.id);
            if (!loaded) return item;
            return {
              ...item,
              cardCount: loaded.total,
              snippetsStarted: loaded.started,
              snippetsStartedNotDue: loaded.startedNotDue,
              snippetsMastered: loaded.mastered
            };
          })
        );
      }
    } catch (error) {
      if (isStaleFirestorePermissionError(userId, error)) return;
      console.error('Error loading recent items:', error);
      setError('Failed to load recent items. Retrying...');
      setRecentStudiedSnippets([]);
      setIsLoading(false);
    }
  };

  const loadDeckCards = async (deckId) => {
    try {
      const deckRef = doc(db, `users/${userId}/decks/${deckId}`);
      const deckDoc = await getDoc(deckRef);

      if (!deckDoc.exists()) {
        console.error('Deck not found');
        return;
      }

      const cardsRef = collection(deckRef, 'cards');
      const snapshot = await getDocs(cardsRef);
      const cards = snapshot.docs.map(doc => ({
        id: doc.id,
        ...doc.data()
      }));

      setSelectedDeckCards(cards);
      setDeckPreviewMode(true);
    } catch (error) {
      console.error('Error loading deck cards:', error);
    }
  };

  const handleItemClick = (item) => {
    onStartStudying(item.id);
  };

  if (selectedDeck && !deckPreviewMode) {
    return (
      <DeckEditor
        userId={userId}
        deckId={selectedDeck.id}
      />
    );
  }

  if (deckPreviewMode && selectedDeckCards) {
    return (
      <div className="deck-preview">
        <div className="deck-preview-header">
          <button className="back-button" onClick={() => {
            setDeckPreviewMode(false);
            setSelectedDeck(null);
            setSelectedDeckCards(null);
          }}>
            ← Back to Home
          </button>
          <h2>{selectedDeck.name}</h2>
          <button
            className="start-study-button"
            onClick={() => onStartStudying(selectedDeck.id)}
          >
            Start Study Session
          </button>
        </div>
        <div className="cards-list">
          {selectedDeckCards.map((card, index) => (
            <div key={card.id} className="card-preview">
              <div className="card-number">#{index + 1}</div>
              <div className="card-content">
                <h3>{card.title}</h3>
                <p>{card.description}</p>
              </div>
            </div>
          ))}
        </div>
      </div>
    );
  }

  // Add this new function to trigger a refresh
  const refreshHome = () => {
    setRefreshKey(prev => prev + 1);
  };

  // Function to truncate title text to fit in one line
  const truncateTitle = (title, maxLength = 85) => {
    if (!title) return '';
    if (title.length <= maxLength) return title;
    return title.substring(0, maxLength).trim() + '...';
  };

  // Normalize Firestore Timestamp or Date to ms (for sorting by "last interacted")
  const toSortTime = (v) => {
    if (v == null || v === undefined) return 0;
    if (v.toDate && typeof v.toDate === 'function') return v.toDate().getTime();
    if (typeof v.seconds === 'number') return v.seconds * 1000;
    const t = new Date(v).getTime();
    return Number.isNaN(t) ? 0 : t;
  };

  // Most recent interaction: prefer lastAccessed / lastAccessedAt / updatedAt, then createdAt
  const getSortTime = (item) => {
    const candidates = [
      item.lastAccessed,
      item.lastAccessedAt,
      item.updatedAt,
      item.createdAt
    ].map(toSortTime);
    return Math.max(0, ...candidates);
  };

  // "In Progress" cookbooks sorted by most recently interacted
  const handleOpenCreation = useCallback(() => {
    if (typeof onOpenCreation === 'function') {
      onOpenCreation();
      return;
    }
    setView?.('creation');
  }, [onOpenCreation, setView]);

  const inProgressItems = useMemo(() => {
    const cookbooks = (recentItems || []).map(item => ({ ...item, itemType: 'cookbook' }));
    cookbooks.sort((a, b) => getSortTime(b) - getSortTime(a));
    return cookbooks.slice(0, IN_PROGRESS_LIMIT);
  }, [recentItems]);

  return (
    <div className="home-container">
      <div className="home-manager">
        {isAnonymousGuest && guestShowBrowseTracksCta && typeof onGuestBrowseTracks === 'function' && (
          <div className="home-page-guest-demo-cta">
            <button
              type="button"
              className="home-page-guest-demo-cta__button"
              onClick={onGuestBrowseTracks}
            >
              <span className="home-page-guest-demo-cta__grid" aria-hidden />
              <span className="home-page-guest-demo-cta__glow" aria-hidden />
              <span className="home-page-guest-demo-cta__leading">
                <span className="home-page-guest-demo-cta__icon-wrap" aria-hidden>
                  <GuestDemoLearnIcon className="home-page-guest-demo-cta__icon" />
                </span>
                <span className="home-page-guest-demo-cta__text">
                  <span className="home-page-guest-demo-cta__title">
                    See what you can learn.
                  </span>
                  <span className="home-page-guest-demo-cta__steps">
                    <span className="home-page-guest-demo-cta__step">
                      <span className="home-page-guest-demo-cta__step-num">1</span>
                      Pick a track
                    </span>
                    <span className="home-page-guest-demo-cta__step">
                      <span className="home-page-guest-demo-cta__step-num">2</span>
                      Type it from memory
                    </span>
                    <span className="home-page-guest-demo-cta__step">
                      <span className="home-page-guest-demo-cta__step-num">3</span>
                      Review on schedule
                    </span>
                  </span>
                </span>
              </span>
              <span className="home-page-guest-demo-cta__action" aria-hidden>
                <span className="home-page-guest-demo-cta__action-text">
                  Browse tracks
                </span>
                <ChevronRight className="home-page-guest-demo-cta__action-arrow" size={16} strokeWidth={2} />
              </span>
            </button>
          </div>
        )}
        {error && (
          <div className="error-container">
            <p className="error-message">{error}</p>
            {retryCount < MAX_RETRIES && (
              <button className="retry-button" onClick={handleRetry}>
                Retry
              </button>
            )}
          </div>
        )}

        {!error && (
          <>
            {viewMode === 'recents' ? (
              <>
                <section className="home-page-primary-tabs-panel" aria-label="Pick up where you left off">
                  <div className="home-page-primary-tabs-panel__tablist" role="tablist" aria-label="Home sections">
                    {!isAnonymousGuest && primaryRole ? (
                      <button
                        type="button"
                        role="tab"
                        id="home-primary-tab-role-path"
                        aria-selected={homePrimaryTab === 'role-path'}
                        aria-controls="home-primary-panel-role-path"
                        className={`home-page-primary-tabs-panel__tab home-page-primary-tabs-panel__tab--role ${homePrimaryTab === 'role-path' ? 'home-page-primary-tabs-panel__tab--active' : ''}`}
                        onClick={() => setHomePrimaryTab('role-path')}
                      >
                        <span className="home-page-primary-tabs-panel__tab-icon home-page-primary-tabs-panel__tab-icon--role" aria-hidden>
                          {primaryRole.iconClass ? (
                            <i className={`ti ${primaryRole.iconClass}`} />
                          ) : (
                            <i className="ti ti-user" />
                          )}
                        </span>
                        <span className="home-page-primary-tabs-panel__tab-label">{primaryRole.title}</span>
                      </button>
                    ) : null}
                    <button
                      type="button"
                      role="tab"
                      id="home-primary-tab-roadmap"
                      aria-selected={homePrimaryTab === 'roadmap'}
                      aria-controls="home-primary-panel-roadmap"
                      className={`home-page-primary-tabs-panel__tab ${homePrimaryTab === 'roadmap' ? 'home-page-primary-tabs-panel__tab--active' : ''}`}
                      onClick={() => setHomePrimaryTab('roadmap')}
                    >
                      <HomePrimaryTabRoadmapIcon
                        className="home-page-primary-tabs-panel__tab-icon home-page-primary-tabs-panel__tab-icon--cookbook"
                        aria-hidden
                      />
                      <span className="home-page-primary-tabs-panel__tab-label">Cookbooks</span>
                    </button>
                    {showHomeTracksTab ? (
                      <button
                        type="button"
                        role="tab"
                        id="home-primary-tab-tracks"
                        aria-selected={homePrimaryTab === 'tracks'}
                        aria-controls="home-primary-panel-tracks"
                        className={`home-page-primary-tabs-panel__tab ${homePrimaryTab === 'tracks' ? 'home-page-primary-tabs-panel__tab--active' : ''}`}
                        onClick={() => setHomePrimaryTab('tracks')}
                      >
                        <RoadmapNodeIcon
                          className="home-page-primary-tabs-panel__tab-icon home-page-primary-tabs-panel__tab-icon--track app-side-nav-roadmap-icon"
                          aria-hidden
                        />
                        <span className="home-page-primary-tabs-panel__tab-label">Tracks</span>
                      </button>
                    ) : null}
                    <button
                      type="button"
                      role="tab"
                      id="home-primary-tab-last-session"
                      aria-selected={homePrimaryTab === 'last-session'}
                      aria-controls="home-primary-panel-last-session"
                      className={`home-page-primary-tabs-panel__tab ${homePrimaryTab === 'last-session' ? 'home-page-primary-tabs-panel__tab--active' : ''}`}
                      onClick={() => setHomePrimaryTab('last-session')}
                    >
                      <HomePrimaryTabSnippetIcon
                        className="home-page-primary-tabs-panel__tab-icon"
                        aria-hidden
                      />
                      <span className="home-page-primary-tabs-panel__tab-label">Snippets</span>
                    </button>
                  </div>
                  {!isAnonymousGuest && primaryRole ? (
                    <div
                      id="home-primary-panel-role-path"
                      role="tabpanel"
                      aria-labelledby="home-primary-tab-role-path"
                      hidden={homePrimaryTab !== 'role-path'}
                      className="home-page-primary-tabs-panel__panel"
                    >
                      <div className="home-page-role-path-panel">
                        <div className="home-page-role-path-panel__head">
                          {primaryRole.promise ? (
                            <div className="home-page-role-path-panel__identity">
                              <p className="home-page-role-path-panel__promise">{primaryRole.promise}</p>
                            </div>
                          ) : null}
                          {rolePathOverviewLoading && !rolePathOverview ? (
                            <div className="home-page-role-path-panel__stats home-page-role-path-panel__stats--loading" aria-hidden>
                              <div className="home-page-role-path-panel__stat-skeleton" />
                              <div className="home-page-role-path-panel__stat-skeleton" />
                              <div className="home-page-role-path-panel__stat-skeleton" />
                            </div>
                          ) : rolePathOverview ? (
                            <div className="home-page-role-path-panel__stats" aria-label="Role path progress">
                              <div className="home-page-role-path-panel__stat">
                                <span className="home-page-role-path-panel__stat-value">
                                  {rolePathOverview.snippetsStarted}
                                  <span className="home-page-role-path-panel__stat-total">
                                    /{rolePathOverview.snippetsTotal}
                                  </span>
                                </span>
                                <span className="home-page-role-path-panel__stat-label">started</span>
                              </div>
                              <div className="home-page-role-path-panel__stat">
                                <span className="home-page-role-path-panel__stat-value">
                                  {rolePathOverview.snippetsMastered}
                                  <span className="home-page-role-path-panel__stat-total">
                                    /{rolePathOverview.snippetsTotal}
                                  </span>
                                </span>
                                <span className="home-page-role-path-panel__stat-label">mastered</span>
                              </div>
                              <div className="home-page-role-path-panel__stat">
                                <span className="home-page-role-path-panel__stat-value">
                                  {rolePathOverview.tracksCompleted}
                                  <span className="home-page-role-path-panel__stat-total">
                                    /{rolePathOverview.tracksTotal}
                                  </span>
                                </span>
                                <span className="home-page-role-path-panel__stat-label">tracks</span>
                              </div>
                            </div>
                          ) : null}
                        </div>

                        <div className="home-page-review-cta home-page-review-cta--role-path">
                          <button
                            type="button"
                            className="home-page-review-cta__button"
                            onClick={openPrimaryRolePath}
                            aria-label={
                              rolePathOverview?.resumeTrackId && !rolePathOverview?.allComplete
                                ? `Continue ${rolePathOverview.resumeTrackTitle}`
                                : `Open ${primaryRole.title} in Learn`
                            }
                          >
                            <span className="home-page-review-cta__leading">
                              <span className="home-page-review-cta__icon-wrap" aria-hidden>
                                {rolePathOverview?.resumeLanguage ? (
                                  <IconRenderer
                                    name={normalizeRoadmapLanguageKey(rolePathOverview.resumeLanguage)}
                                    type="language"
                                    className="home-page-review-cta__icon"
                                    aria-hidden
                                  />
                                ) : primaryRole.iconClass ? (
                                  <i className={`ti ${primaryRole.iconClass} home-page-review-cta__icon home-page-review-cta__icon--role`} />
                                ) : (
                                  <i className="ti ti-user home-page-review-cta__icon home-page-review-cta__icon--role" />
                                )}
                              </span>
                              <span className="home-page-review-cta__text">
                                <span className="home-page-review-cta__title">
                                  {rolePathOverview?.allComplete ? 'View path' : 'Continue learning'}
                                </span>
                                <span className="home-page-review-cta__subtitle">{rolePathSubtitle}</span>
                              </span>
                            </span>
                            <span className="home-page-review-cta__action" aria-hidden>
                              <span className="home-page-review-cta__action-text">{rolePathActionLabel}</span>
                              <ChevronRight
                                className="home-page-review-cta__action-arrow"
                                size={16}
                                strokeWidth={2}
                                aria-hidden
                              />
                            </span>
                          </button>
                        </div>

                        {rolePathOverviewLoading ? (
                          <p className="home-page-role-path-panel__tracks-loading">Loading tracks…</p>
                        ) : rolePathTracks.length ? (
                          <>
                            <ol
                              className="home-page-role-path-panel__track-list"
                              aria-label="Tracks in your role path"
                            >
                              {rolePathTracksPageRows.map((trackRow, pageIndex) => {
                                const index =
                                  (rolePathTrackPage - 1) * HOME_ROLE_PATH_TRACKS_PAGE_SIZE + pageIndex;
                                const langKey = normalizeRoadmapLanguageKey(trackRow.language || 'other');
                                const trackTitle = formatRoadmapTrackTitle(langKey, trackRow.trackTitle, {
                                  language: trackRow.language
                                });
                                const pct =
                                  trackRow.snippetTotal > 0
                                    ? Math.round((trackRow.snippetMastered / trackRow.snippetTotal) * 100)
                                    : 0;
                                const trackRef = {
                                  trackId: trackRow.trackId,
                                  language: trackRow.language,
                                  title: trackRow.trackTitle
                                };
                                const isLocked = !userCanAccessRoadmapTrack(
                                  isPremiumUser,
                                  trackRow.trackId,
                                  trackRef
                                );

                                return (
                                  <li key={trackRow.trackId} className="home-page-role-path-panel__track-item">
                                    <button
                                      type="button"
                                      className={[
                                        'home-page-role-path-panel__track-row',
                                        trackRow.isComplete
                                          ? 'home-page-role-path-panel__track-row--complete'
                                          : '',
                                        isLocked ? 'home-page-role-path-panel__track-row--locked' : ''
                                      ]
                                        .filter(Boolean)
                                        .join(' ')}
                                      onClick={() => openRoleTrackInLearn(trackRow)}
                                      aria-label={
                                        isLocked
                                          ? `Preview ${trackTitle} (Pro track)`
                                          : `Open ${trackTitle} in Learn`
                                      }
                                    >
                                      <span className="home-page-role-path-panel__track-order" aria-hidden>
                                        {String(index + 1).padStart(2, '0')}
                                      </span>
                                      <IconRenderer
                                        name={langKey}
                                        type="language"
                                        size="small"
                                        className="home-page-role-path-panel__track-lang-icon"
                                        aria-hidden
                                      />
                                      <span className="home-page-role-path-panel__track-main">
                                        <span className="home-page-role-path-panel__track-title">
                                          {trackTitle}
                                        </span>
                                        <span className="home-page-role-path-panel__track-meta">
                                          {trackRow.snippetStarted}/{trackRow.snippetTotal} started
                                        </span>
                                      </span>
                                      {trackRow.snippetTotal > 0 ? (
                                        <div
                                          className="home-page-role-path-panel__track-progress"
                                          role="progressbar"
                                          aria-valuenow={trackRow.snippetMastered}
                                          aria-valuemin={0}
                                          aria-valuemax={trackRow.snippetTotal}
                                          aria-label={formatSnippetMasteryCountAria(trackRow.snippetMastered, trackRow.snippetTotal)}
                                        >
                                          <div className="home-page-role-path-panel__track-progress-track">
                                            <div
                                              className="home-page-role-path-panel__track-progress-fill"
                                              style={{ width: `${pct}%` }}
                                            />
                                          </div>
                                          <span className="home-page-role-path-panel__track-progress-label">
                                            {pct}%
                                          </span>
                                        </div>
                                      ) : null}
                                      {trackRow.isComplete ? (
                                        <AchievementTrophyIcon
                                          className="home-page-role-path-panel__track-trophy"
                                          aria-label="Track complete"
                                        />
                                      ) : null}
                                    </button>
                                  </li>
                                );
                              })}
                            </ol>
                            {rolePathTrackTotalPages > 1 ? (
                              <nav
                                className="home-page-role-path-panel__track-pagination"
                                aria-label="Role path tracks pages"
                              >
                                <button
                                  type="button"
                                  className="home-page-role-path-panel__track-pagination-btn"
                                  disabled={rolePathTrackPage <= 1}
                                  onClick={() => setRolePathTrackPage((p) => Math.max(1, p - 1))}
                                  aria-label="Previous tracks page"
                                >
                                  <ChevronLeft
                                    className="home-page-role-path-panel__track-pagination-icon"
                                    size={16}
                                    strokeWidth={2}
                                  />
                                </button>
                                <span className="home-page-role-path-panel__track-pagination-info">
                                  {rolePathTrackPage} / {rolePathTrackTotalPages}
                                </span>
                                <button
                                  type="button"
                                  className="home-page-role-path-panel__track-pagination-btn"
                                  disabled={rolePathTrackPage >= rolePathTrackTotalPages}
                                  onClick={() =>
                                    setRolePathTrackPage((p) => Math.min(rolePathTrackTotalPages, p + 1))
                                  }
                                  aria-label="Next tracks page"
                                >
                                  <ChevronRight
                                    className="home-page-role-path-panel__track-pagination-icon"
                                    size={16}
                                    strokeWidth={2}
                                  />
                                </button>
                              </nav>
                            ) : null}
                          </>
                        ) : (
                          <div className="home-page-primary-tabs-panel__empty">
                            <p className="home-page-primary-tabs-panel__empty-text">No tracks on this path yet</p>
                            <p className="home-page-primary-tabs-panel__empty-hint">
                              Open Learn to browse your role path.
                            </p>
                          </div>
                        )}
                      </div>
                    </div>
                  ) : null}
                  {showHomeTracksTab ? (
                    <div
                      id="home-primary-panel-tracks"
                      role="tabpanel"
                      aria-labelledby="home-primary-tab-tracks"
                      hidden={homePrimaryTab !== 'tracks'}
                      className="home-page-primary-tabs-panel__panel"
                    >
                      <div className="home-page-role-path-panel home-page-tracks-panel">
                        <p className="home-page-primary-tabs-panel__tagline home-page-primary-tabs-panel__tagline--block">
                          Continue Learning
                        </p>
                        <div className="home-page-review-cta">
                          <button
                            type="button"
                            className="home-page-review-cta__button"
                            onClick={() => (setView ? setView('home-srs') : setViewMode('srs'))}
                            aria-label={
                              dueItemsCount > 0
                                ? `Go to review page, ${dueItemsCount} snippet${dueItemsCount === 1 ? '' : 's'} due`
                                : 'Go to review page'
                            }
                          >
                            <span className="home-page-review-cta__leading">
                              <span className="home-page-review-cta__icon-wrap" aria-hidden>
                                <RefreshIcon className="home-page-review-cta__icon" />
                              </span>
                              <span className="home-page-review-cta__text">
                                <span className="home-page-review-cta__title">Review</span>
                              </span>
                            </span>
                            <span className="home-page-review-cta__action" aria-hidden>
                              <span className="home-page-review-cta__action-text">Go to Review</span>
                              {dueItemsCount > 0 ? (
                                <span
                                  className="home-page-review-cta__action-count"
                                  title={`${dueItemsCount} snippet${dueItemsCount === 1 ? '' : 's'} due`}
                                >
                                  <span className="home-page-review-cta__action-count-value">
                                    {dueItemsCount > 99 ? '99+' : dueItemsCount}
                                  </span>
                                </span>
                              ) : null}
                              <ChevronRight
                                className="home-page-review-cta__action-arrow"
                                size={16}
                                strokeWidth={2}
                                aria-hidden
                              />
                            </span>
                          </button>
                        </div>

                        {homeGuestTracksLoading ? (
                          <p className="home-page-role-path-panel__tracks-loading">Loading tracks…</p>
                        ) : homeGuestTracks.length ? (
                          <ol
                            className="home-page-role-path-panel__track-list"
                            aria-label="Tracks you started"
                          >
                            {homeGuestTracks.map((trackRow, index) => {
                              const langKey = normalizeRoadmapLanguageKey(trackRow.language || 'other');
                              const trackTitle = formatRoadmapTrackTitle(langKey, trackRow.trackTitle, {
                                language: trackRow.language
                              });
                              const pct =
                                trackRow.snippetTotal > 0
                                  ? Math.round((trackRow.snippetMastered / trackRow.snippetTotal) * 100)
                                  : 0;
                              const trackRef = {
                                trackId: trackRow.trackId,
                                language: trackRow.language,
                                title: trackRow.trackTitle
                              };
                              const isLocked = !userCanAccessRoadmapTrack(
                                isPremiumUser,
                                trackRow.trackId,
                                trackRef
                              );

                              return (
                                <li key={trackRow.trackId} className="home-page-role-path-panel__track-item">
                                  <button
                                    type="button"
                                    className={[
                                      'home-page-role-path-panel__track-row',
                                      trackRow.isComplete
                                        ? 'home-page-role-path-panel__track-row--complete'
                                        : '',
                                      isLocked ? 'home-page-role-path-panel__track-row--locked' : ''
                                    ]
                                      .filter(Boolean)
                                      .join(' ')}
                                    onClick={() => openGuestTrackInLearn(trackRow)}
                                    aria-label={
                                      isLocked
                                        ? `Preview ${trackTitle} (Pro track)`
                                        : `Open ${trackTitle} in Learn`
                                    }
                                  >
                                    <span className="home-page-role-path-panel__track-order" aria-hidden>
                                      {String(index + 1).padStart(2, '0')}
                                    </span>
                                    <IconRenderer
                                      name={langKey}
                                      type="language"
                                      size="small"
                                      className="home-page-role-path-panel__track-lang-icon"
                                      aria-hidden
                                    />
                                    <span className="home-page-role-path-panel__track-main">
                                      <span className="home-page-role-path-panel__track-title">
                                        {trackTitle}
                                      </span>
                                      <span className="home-page-role-path-panel__track-meta">
                                        {trackRow.snippetStarted} snippet
                                        {trackRow.snippetStarted === 1 ? '' : 's'} started
                                      </span>
                                    </span>
                                    {trackRow.snippetTotal > 0 ? (
                                      <div
                                        className="home-page-role-path-panel__track-progress"
                                        role="progressbar"
                                        aria-valuenow={trackRow.snippetMastered}
                                        aria-valuemin={0}
                                        aria-valuemax={trackRow.snippetTotal}
                                        aria-label={formatSnippetMasteryCountAria(
                                          trackRow.snippetMastered,
                                          trackRow.snippetTotal
                                        )}
                                      >
                                        <div className="home-page-role-path-panel__track-progress-track">
                                          <div
                                            className="home-page-role-path-panel__track-progress-fill"
                                            style={{ width: `${pct}%` }}
                                          />
                                        </div>
                                        <span className="home-page-role-path-panel__track-progress-label">
                                          {pct}%
                                        </span>
                                      </div>
                                    ) : null}
                                    {trackRow.isComplete ? (
                                      <AchievementTrophyIcon
                                        className="home-page-role-path-panel__track-trophy"
                                        aria-label="Track complete"
                                      />
                                    ) : null}
                                  </button>
                                </li>
                              );
                            })}
                          </ol>
                        ) : (
                          <div className="home-page-primary-tabs-panel__empty">
                            <p className="home-page-primary-tabs-panel__empty-text">No tracks started yet</p>
                            <p className="home-page-primary-tabs-panel__empty-hint">
                              Browse Learn and start a track to see it here.
                            </p>
                          </div>
                        )}
                      </div>
                    </div>
                  ) : null}
                  <div
                    id="home-primary-panel-last-session"
                    role="tabpanel"
                    aria-labelledby="home-primary-tab-last-session"
                    hidden={homePrimaryTab !== 'last-session'}
                    className="home-page-primary-tabs-panel__panel"
                  >
                    <div className="home-page-primary-tabs-panel__panel-head">
                      <p className="home-page-primary-tabs-panel__tagline">Continue where you left off</p>
                      {setView ? (
                        <button
                          type="button"
                          className="home-page-last-session__see-all"
                          onClick={() => setView('profile')}
                        >
                          See all
                        </button>
                      ) : null}
                    </div>
                    <div className="home-page-primary-tabs-panel__panel-body home-page-last-session home-page-last-session--in-primary-tab">
                      {isLoading ? (
                        <ul
                          className="user-profile-achievement-stats-panel__snippet-list home-page-last-session__skills-list home-page-last-session__skills-list--loading"
                          aria-busy="true"
                        >
                          {Array.from({ length: HOME_RECENT_STUDIED_SNIPPETS_LIMIT }, (_, i) => (
                            <li
                              key={`home-page-last-session-sk-${i}`}
                              className="user-profile-achievement-stats-panel__snippet-row-item"
                            >
                              <div
                                className="user-profile-achievement-stats-panel__snippet-row home-page-last-session__skills-row-skeleton"
                                aria-hidden="true"
                              >
                                <div className="home-page-last-session__skills-skeleton-body">
                                  <span className="home-page-last-session__skills-skeleton-title" />
                                  <span className="home-page-last-session__skills-skeleton-sub" />
                                </div>
                                <span className="home-page-last-session__skills-skeleton-pills" />
                              </div>
                            </li>
                          ))}
                        </ul>
                      ) : recentStudiedSnippets.length === 0 ? (
                        <div className="home-page-primary-tabs-panel__empty">
                          <p className="home-page-primary-tabs-panel__empty-text">No recent snippets yet</p>
                          <p className="home-page-primary-tabs-panel__empty-hint">
                            Study a snippet to resume it here.
                          </p>
                        </div>
                      ) : (
                        <ul className="user-profile-achievement-stats-panel__snippet-list home-page-last-session__skills-list">
                          {recentStudiedSnippets.map((row) => (
                            <li
                              key={`${row.deckId}_${row.cardId}`}
                              className="user-profile-achievement-stats-panel__snippet-row-item"
                            >
                              <button
                                type="button"
                                className="user-profile-achievement-stats-panel__snippet-row"
                                onClick={() => handleOpenRecentSnippet(row)}
                                aria-label={`Continue studying ${row.snippetTitle}`}
                              >
                                <div className="user-profile-achievement-stats-panel__snippet-body">
                                  <div className="user-profile-achievement-stats-panel__snippet-title-row">
                                    <IconRenderer
                                      name={homeLastSessionSnippetIconLanguage(row)}
                                      type="language"
                                      size="small"
                                      className="user-profile-achievement-stats-panel__snippet-lang-icon"
                                      aria-hidden
                                    />
                                    <span className="user-profile-achievement-stats-panel__snippet-title">
                                      {row.snippetTitle}
                                    </span>
                                  </div>
                                  <div className="user-profile-achievement-stats-panel__snippet-deck-row">
                                    <div className="user-profile-achievement-stats-panel__snippet-deck-info">
                                      <HomeInProgressSnippetIcon
                                        className="user-profile-achievement-stats-panel__snippet-deck-icon"
                                        aria-hidden
                                      />
                                      <span className="user-profile-achievement-stats-panel__snippet-deck-path">
                                        {row.deckPathLabel}
                                      </span>
                                    </div>
                                    <div
                                      className="user-profile-achievement-stats-panel__snippet-srs-status"
                                      aria-label={`Review status: ${row.reviewLabel || 'Not started'}`}
                                    >
                                      <span
                                        className={`user-profile-achievement-stats-panel__snippet-srs-status-pill user-profile-achievement-stats-panel__snippet-srs-status-pill--${cookbookSnippetSrsStatusPillVariant(row)}`}
                                      >
                                        {row.reviewLabel || 'Not started'}
                                      </span>
                                    </div>
                                  </div>
                                </div>
                              </button>
                            </li>
                          ))}
                        </ul>
                      )}
                    </div>
                  </div>

                  <div
                    id="home-primary-panel-roadmap"
                    role="tabpanel"
                    aria-labelledby="home-primary-tab-roadmap"
                    hidden={homePrimaryTab !== 'roadmap'}
                    className="home-page-primary-tabs-panel__panel"
                  >
                    <p className="home-page-primary-tabs-panel__tagline home-page-primary-tabs-panel__tagline--block">
                      Continue Learning
                    </p>
                    <div className="home-page-review-cta">
                      <button
                        type="button"
                        className="home-page-review-cta__button"
                        onClick={() => (setView ? setView('home-srs') : setViewMode('srs'))}
                        aria-label={
                          dueItemsCount > 0
                            ? `Go to review page, ${dueItemsCount} snippet${dueItemsCount === 1 ? '' : 's'} due`
                            : 'Go to review page'
                        }
                      >
                        <span className="home-page-review-cta__leading">
                          <span className="home-page-review-cta__icon-wrap" aria-hidden>
                            <RefreshIcon className="home-page-review-cta__icon" />
                          </span>
                          <span className="home-page-review-cta__text">
                            <span className="home-page-review-cta__title">Review</span>
                            {/* <span className="home-page-review-cta__subtitle">
                              Stay consistent. Build fluency.
                            </span> */}
                          </span>
                        </span>
                        <span className="home-page-review-cta__action" aria-hidden>
                          <span className="home-page-review-cta__action-text">Go to Review</span>
                          {dueItemsCount > 0 ? (
                            <span
                              className="home-page-review-cta__action-count"
                              title={`${dueItemsCount} snippet${dueItemsCount === 1 ? '' : 's'} due`}
                            >
                              <span className="home-page-review-cta__action-count-value">
                                {dueItemsCount > 99 ? '99+' : dueItemsCount}
                              </span>
                            </span>
                          ) : null}
                          <ChevronRight
                            className="home-page-review-cta__action-arrow"
                            size={16}
                            strokeWidth={2}
                            aria-hidden
                          />
                        </span>
                      </button>
                    </div>
                    <div className="home-recent-cookbooks__wrap home-recent-cookbooks__wrap--under-roadmap-tab">
                      {isLoading ? (
                        <div className="home-in-progress__loading recents-grid" aria-busy="true">
                          {Array.from({ length: HOME_COOKBOOK_SLOT_COUNT }, (_, i) => (
                            <div
                              key={`sk-${i}`}
                              className="deck-item home-in-progress__card--roadmap-row home-in-progress__card--cookbook home-in-progress__skeleton"
                              aria-hidden="true"
                            >
                              <div className="home-in-progress__roadmap-row-main">
                                <div className="home-in-progress__row-leading">
                                  <div className="home-in-progress__skeleton-icon home-in-progress__skeleton-icon--row" />
                                  <div className="home-in-progress__cookbook-title-block">
                                    <div className="home-in-progress__skeleton-title home-in-progress__skeleton-title--row" />
                                    <div className="home-in-progress__skeleton-stats home-in-progress__skeleton-stats--row" />
                                  </div>
                                </div>
                              </div>
                            </div>
                          ))}
                        </div>
                      ) : (
                        <div className="recents-grid">
                          {inProgressItems.map(item => (
                            <div
                              key={`cookbook-${item.id}`}
                              className="deck-item recent-deck home-in-progress__card home-in-progress__card--roadmap-row home-in-progress__card--cookbook"
                              onClick={() => handleItemClick(item)}
                            >
                              <div className="home-in-progress__roadmap-row-main">
                                <div className="home-in-progress__row-leading">
                                  <IconRenderer
                                    name={homeInProgressIconLanguage(item)}
                                    type="language"
                                    size="large"
                                    className="home-in-progress__row-language-icon"
                                    style={HOME_LANG_ICON_ROW_STYLE}
                                  />
                                  <div className="home-in-progress__cookbook-title-block">
                                    <h3 className="home-in-progress__row-title">
                                      {truncateTitle(item.name || item.title)}
                                    </h3>
                                    <CookbookSnippetProgressStats
                                      className="home-in-progress__cookbook-progress"
                                      started={item.snippetsStarted}
                                      mastered={item.snippetsMastered}
                                      total={item.cardCount}
                                      loading={item.cardCount === undefined}
                                      cookbookTitle={item.name || item.title}
                                      onStartedClick={(e) => handlePracticeStartedCookbook(e, item)}
                                      startedDisabled={
                                        practiceLoadingDeckId === item.id ||
                                        !item.snippetsStarted
                                      }
                                    />
                                  </div>
                                </div>
                              </div>
                            </div>
                          ))}
                          {Array.from(
                            { length: Math.max(0, HOME_COOKBOOK_SLOT_COUNT - inProgressItems.length) },
                            (_, i) => (
                              <button
                                key={`cookbook-slot-${i}`}
                                type="button"
                                className="deck-item recent-deck home-in-progress__card home-in-progress__card--roadmap-row home-in-progress__card--cookbook home-in-progress__card--cookbook-slot"
                                onClick={handleOpenCreation}
                                aria-label="Create cookbook or snippet"
                              >
                                <div className="home-in-progress__roadmap-row-main home-in-progress__card--cookbook-slot-main">
                                  <div className="home-in-progress__row-leading home-in-progress__card--cookbook-slot-leading">
                                    <span
                                      className="home-in-progress__card--cookbook-slot-icon-placeholder"
                                      aria-hidden="true"
                                    >
                                      <Plus className="home-in-progress__card--cookbook-slot-plus-icon" size={16} strokeWidth={1.75} />
                                    </span>
                                    <span className="home-in-progress__card--cookbook-slot-title-text">
                                      Add cookbook
                                    </span>
                                  </div>
                                </div>
                              </button>
                            )
                          )}
                        </div>
                      )}
                    </div>
                  </div>
                </section>
              </>
            ) : (
              <SRSDashboard
                dueDecks={dueDecks}
                upcomingReviews={upcomingReviews}
                trackSnippetRows={trackSnippetRows}
                trackUpcomingReviews={trackUpcomingReviews}
                loadingSrs={loadingSrs}
                loadError={srsLoadError}
                onRetryLoad={() => setRefreshKey((k) => k + 1)}
                handleStudyDeck={handleStudyDeck}
              />
            )}
          </>
        )}
      </div>

      {/* Home Progress Footer */}
      <HomeProgressFooter userId={userId} />
    </div>
  );
};

export default Home;
